import type User from '@this/domain/user/user';

interface VersionMeta {
  commit?: string;
  latestPr?: string;
  prUrl?: string;
  buildTime?: string;
  commitMessage?: string;
  version?: string;
}

const getVersionMeta = (): VersionMeta | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const version = window.APP_VERSION;
  if (!version || typeof version !== 'object') {
    return undefined;
  }

  const { commit, latestPr, prUrl, buildTime, commitMessage, version: versionHash } = version;

  if (commit || latestPr || prUrl || buildTime || commitMessage || versionHash) {
    return { commit, latestPr, prUrl, buildTime, commitMessage, version: versionHash };
  }

  return undefined;
};

const buildMetaData = (base?: Record<string, unknown>): Record<string, unknown> | undefined => {
  const versionMeta = getVersionMeta();

  const metaData: Record<string, unknown> = {};

  if (versionMeta) {
    metaData.version = versionMeta;
  }

  if (base) {
    Object.assign(metaData, base);
  }

  return Object.keys(metaData).length > 0 ? metaData : undefined;
};

export const setupBugsnag = (user: User) => {
  if (typeof bugsnagClient === 'undefined') {
    return;
  }

  bugsnagClient.user = {
    id: user.id,
    name: user.name,
    email: user.email
  };
};

export const reportError = (e: unknown) => {
  if (typeof bugsnagClient === 'undefined') {
    return;
  }

  if (e instanceof Error) {
    const metaData = buildMetaData();
    if (metaData) {
      bugsnagClient.notify(e, { metaData, severity: 'error' });
    } else {
      bugsnagClient.notify(e, { severity: 'error' });
    }
    return;
  }

  let errorObj: Error;
  let originalErrorMeta: Record<string, unknown> = {};

  if (e !== null && typeof e === 'object') {
    errorObj = new Error('Non-Error object was thrown');
    originalErrorMeta = {
      originalErrorType: 'object',
      originalErrorValue: e
    };
  } else if (typeof e === 'string') {
    errorObj = new Error(e);
    originalErrorMeta = {
      originalErrorType: 'string'
    };
  } else {
    errorObj = new Error(`Non-Error value was thrown: ${String(e)}`);
    originalErrorMeta = {
      originalErrorType: typeof e,
      originalErrorValue: e
    };
  }

  const metaData = buildMetaData({ originalError: originalErrorMeta });

  bugsnagClient.notify(errorObj, {
    metaData: metaData || { originalError: originalErrorMeta },
    severity: 'error'
  });
};
